/**
 * Partner-pairing UI: create/share an invite (mom), enter a code (partner),
 * and manage an existing connection. The invite code is the end-to-end
 * encryption key, so it is only ever shown on-device and shared by the user.
 */
import { useEffect, useState } from 'react';
import { toDataURL as qrToDataURL } from 'qrcode';
import { useLocale, useT } from '../i18n/i18n';
import { useActions, useApp } from '../lib/store';
import { CODE_LENGTH, formatCode, normalizeCode } from '../lib/crypto';
import { buildJoinUrl } from '../lib/pairing-link';
import { formatDate } from '../lib/time';
import type { SyncStatus } from '../lib/sync';

const STATUS_DOT: Record<SyncStatus, string> = {
  idle: 'text-good',
  syncing: 'text-accent animate-pulse-soft',
  offline: 'text-warn',
  error: 'text-bad',
  unpaired: 'text-ink-faint',
};

/** "ABCD1" → "ABCD-1"; groups into blocks of 4 and strips anything not in the alphabet. */
function prettyCode(raw: string): string {
  return formatCode(normalizeCode(raw).slice(0, CODE_LENGTH));
}

/**
 * Invite-code entry with join handling. Used here and by onboarding, so both
 * flows accept codes with or without the dash and share the same errors.
 */
export function PairCodeEntry({
  initialCode,
  onJoined,
}: {
  initialCode?: string | null;
  onJoined?: () => void;
}) {
  const t = useT();
  const { joinPair } = useActions();
  const [value, setValue] = useState(() => (initialCode ? prettyCode(initialCode) : ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const code = normalizeCode(value);

  const submit = async () => {
    if (busy) return;
    if (code.length !== CODE_LENGTH) {
      setError(t.pairing.errors.invalidCode);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await joinPair(code);
      onJoined?.();
    } catch (err) {
      setError(err instanceof TypeError ? t.pairing.errors.unreachable : t.pairing.errors.joinFailed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="mb-1 block text-sm font-bold text-ink-soft" htmlFor="pair-code-input">
        {t.pairing.codeLabel}
      </label>
      <input
        id="pair-code-input"
        type="text"
        inputMode="text"
        autoCapitalize="characters"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder={t.pairing.codePlaceholder}
        value={value}
        onChange={(e) => {
          setValue(prettyCode(e.target.value));
          setError(null);
        }}
        className="w-full rounded-2xl border border-line bg-bg px-4 py-3 text-center font-mono text-xl tracking-widest text-ink"
      />
      {error ? <p className="mt-2 text-sm text-bad">{error}</p> : null}
      <button
        type="submit"
        disabled={busy || code.length !== CODE_LENGTH}
        className="btn-primary mt-3 min-h-[48px] w-full py-3 disabled:opacity-50"
      >
        {busy ? t.pairing.connecting : t.pairing.connect}
      </button>
    </form>
  );
}

/** Large code + QR + share/copy, shown after creating an invite. */
function InviteView({ code }: { code: string }) {
  const t = useT();
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    qrToDataURL(buildJoinUrl(code), { width: 480, margin: 2 })
      .then((url) => {
        if (alive) setQrUrl(url);
      })
      .catch(() => {
        if (alive) setQrUrl(null);
      });
    return () => {
      alive = false;
    };
  }, [code]);

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const shareOrCopy = async () => {
    const url = buildJoinUrl(code);
    try {
      if (canShare) {
        await navigator.share({ url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // user dismissed the share sheet; nothing to do
    }
  };

  return (
    <div className="animate-float-up space-y-3">
      <h4 className="text-sm text-ink-soft">{t.pairing.inviteTitle}</h4>
      <p className="break-words text-center font-mono text-2xl font-bold tracking-wider text-primary">
        {formatCode(code)}
      </p>
      {qrUrl ? (
        <img src={qrUrl} alt={t.pairing.qrAlt} className="mx-auto w-48 max-w-full rounded-2xl" />
      ) : null}
      <p className="text-sm text-ink-soft">{t.pairing.scanHint}</p>
      <button
        type="button"
        onClick={() => void shareOrCopy()}
        className="btn-secondary min-h-[48px] w-full py-3"
      >
        {copied ? t.pairing.copied : canShare ? t.pairing.shareLink : t.pairing.copyLink}
      </button>
      <p className="text-xs text-ink-faint">{t.pairing.keyWarning}</p>
    </div>
  );
}

export default function PairingPanel() {
  const t = useT();
  const locale = useLocale();
  const { device, pairing, syncStatus } = useApp();
  const { createPair, syncNow, unpair } = useActions();

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const role = device?.role ?? 'mom';

  const create = async () => {
    if (creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      await createPair();
      setShowInvite(true);
    } catch (err) {
      setCreateError(
        err instanceof TypeError ? t.pairing.errors.unreachable : t.pairing.errors.createFailed,
      );
    } finally {
      setCreating(false);
    }
  };

  // ── Not paired yet ────────────────────────────────────────────────────
  if (!pairing) {
    if (role === 'partner') {
      return (
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">{t.pairing.enterHint}</p>
          <PairCodeEntry />
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <p className="text-sm text-ink-soft">{t.pairing.intro}</p>
        {createError ? <p className="text-sm text-bad">{createError}</p> : null}
        <button
          type="button"
          disabled={creating}
          onClick={() => void create()}
          className="btn-primary min-h-[48px] w-full py-3 disabled:opacity-50"
        >
          {creating ? t.pairing.creating : t.pairing.createInvite}
        </button>
      </div>
    );
  }

  // ── Paired ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-current ${STATUS_DOT[syncStatus]}`}
        />
        <span className="text-sm font-bold text-ink">{t.common.sync[syncStatus]}</span>
      </div>
      <p className="text-sm text-ink-soft">
        {t.pairing.connectedSince(formatDate(pairing.connectedAt, locale))}
      </p>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void syncNow()}
          className="btn-secondary min-h-[48px] flex-1 py-3"
        >
          {t.pairing.syncNow}
        </button>
        <button
          type="button"
          onClick={() => setConfirmDisconnect(true)}
          className="min-h-[48px] flex-1 rounded-full bg-bad-soft py-3 font-bold text-bad"
        >
          {t.pairing.disconnect}
        </button>
      </div>

      {confirmDisconnect ? (
        <div className="animate-float-up space-y-3 rounded-2xl bg-surface-2 p-4">
          <p className="text-sm text-ink">{t.pairing.disconnectWarning}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmDisconnect(false)}
              className="btn-secondary min-h-[44px] flex-1 py-2.5"
            >
              {t.common.actions.cancel}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmDisconnect(false);
                void unpair();
              }}
              className="min-h-[44px] flex-1 rounded-full bg-bad-soft py-2.5 font-bold text-bad"
            >
              {t.pairing.confirmDisconnect}
            </button>
          </div>
        </div>
      ) : null}

      {role === 'mom' ? (
        showInvite ? (
          <div className="space-y-2">
            <InviteView code={pairing.code} />
            <button
              type="button"
              onClick={() => setShowInvite(false)}
              className="min-h-[44px] w-full py-2 text-sm font-bold text-primary"
            >
              {t.pairing.hideInvite}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowInvite(true)}
            className="min-h-[44px] w-full py-2 text-sm font-bold text-primary"
          >
            {t.pairing.showInvite}
          </button>
        )
      ) : null}
    </div>
  );
}
